import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// App Logo URL
const APP_LOGO = "https://customer-assets.emergentagent.com/job_trend-stock-app-1/artifacts/tmh7qekk_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2001_23_15%20PM.png";

// Navbar Component
const Navbar = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030712]/80 backdrop-blur-xl border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={APP_LOGO} alt="StockTrendFusion" className="w-10 h-10 rounded-xl" />
          <span className="font-semibold text-lg text-white">StockTrendFusion</span>
        </Link>
        <Link 
          to="/" 
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="nav-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  </nav>
);

// Footer
const Footer = () => (
  <footer className="py-12 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src={APP_LOGO} alt="StockTrendFusion" className="w-10 h-10 rounded-xl" />
          <span className="font-semibold text-white">StockTrendFusion</span>
        </div>
        
        <div className="flex items-center gap-8">
          <Link 
            to="/support" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-support-link"
          >
            Support
          </Link>
          <Link 
            to="/privacy" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </Link>
          <Link 
            to="/terms" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-terms-link"
          >
            Terms of Service
          </Link>
        </div>
        
        <p className="text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} StockTrendFusion. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen" data-testid="terms-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-medium tracking-wider uppercase text-emerald-400 mb-4">
              Legal
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-zinc-400">
              Last updated: December 2025
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Content */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div 
            className="prose-dark space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-zinc-400 leading-relaxed">
                By downloading, installing, or using the StockTrendFusion mobile application ("App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="text-zinc-400 leading-relaxed">
                StockTrendFusion provides stock market tracking, portfolio management, analytics, and related financial information services. The App is designed for informational purposes only and does not constitute financial advice, investment recommendations, or professional guidance.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">3. User Accounts</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                To access certain features of the App, you may be required to create an account. You agree to:
              </p>
              <ul className="text-zinc-400 space-y-2 list-disc pl-6">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Subscription and Payments</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                StockTrendFusion offers both free and premium subscription plans. Premium features require a paid subscription.
              </p>
              <ul className="text-zinc-400 space-y-2 list-disc pl-6">
                <li>Subscriptions are billed through Apple's App Store</li>
                <li>Subscriptions automatically renew unless cancelled</li>
                <li>Prices are subject to change with notice</li>
                <li>Refunds are handled according to Apple's refund policy</li>
              </ul>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Disclaimer of Financial Advice</h2>
              <p className="text-zinc-400 leading-relaxed font-medium bg-white/5 p-4 rounded-lg border border-white/10">
                IMPORTANT: StockTrendFusion does not provide financial, investment, tax, or legal advice. All information provided through the App is for informational purposes only. You should consult with qualified professionals before making any investment decisions. Past performance is not indicative of future results.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Acceptable Use</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="text-zinc-400 space-y-2 list-disc pl-6">
                <li>Use the App for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the App or servers</li>
                <li>Reverse engineer or decompile any part of the App</li>
                <li>Share your account with others or use multiple accounts</li>
                <li>Scrape or harvest data from the App</li>
              </ul>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Intellectual Property</h2>
              <p className="text-zinc-400 leading-relaxed">
                All content, features, and functionality of the App are owned by StockTrendFusion and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Data Accuracy</h2>
              <p className="text-zinc-400 leading-relaxed">
                While we strive to provide accurate and timely financial data, we cannot guarantee the accuracy, completeness, or timeliness of any information. Stock prices, market data, and analytics are provided "as is" and may be delayed. Always verify information with official sources before making investment decisions.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
              <p className="text-zinc-400 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, STOCKTRENDFUSION SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR INVESTMENT LOSSES, ARISING OUT OF YOUR USE OF THE APP.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Indemnification</h2>
              <p className="text-zinc-400 leading-relaxed">
                You agree to indemnify and hold harmless StockTrendFusion, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the App or violation of these Terms.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Termination</h2>
              <p className="text-zinc-400 leading-relaxed">
                We reserve the right to suspend or terminate your access to the App at any time, with or without cause, with or without notice. Upon termination, your right to use the App will immediately cease.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to Terms</h2>
              <p className="text-zinc-400 leading-relaxed">
                We may modify these Terms at any time. We will notify you of material changes through the App or by email. Your continued use of the App after changes constitutes acceptance of the modified Terms.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Governing Law</h2>
              <p className="text-zinc-400 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States of America, without regard to conflict of law principles.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Contact Information</h2>
              <p className="text-zinc-400 leading-relaxed">
                If you have any questions about these Terms, please contact us through our{" "}
                <Link to="/support" className="text-emerald-400 hover:text-emerald-300 underline">
                  Support Page
                </Link>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TermsPage;
